import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

interface SubMenuItem {
  name: string;
  href: string;
  description: string;
}

interface SidebarSubmenuProps {
  items: SubMenuItem[];
  position: { x: number; y: number };
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  title?: string;
}

const SidebarSubmenu: React.FC<SidebarSubmenuProps> = ({
  items,
  position,
  onMouseEnter,
  onMouseLeave,
  title,
}) => {
  const location = useLocation();

  const isSubItemActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Calculate menu height based on items
  const estimatedItemHeight = 60; // Approximate height per item including padding
  const titleHeight = title ? 40 : 0;
  const padding = 16;
  const estimatedMenuHeight = Math.min(
    items.length * estimatedItemHeight + titleHeight + padding,
    320 // max-h-80 = 320px
  );

  // Adjust vertical position to prevent overflow
  const viewportHeight = window.innerHeight;
  let adjustedY = position.y;

  // Check if menu would overflow bottom
  if (position.y + estimatedMenuHeight / 2 > viewportHeight - 20) {
    adjustedY = viewportHeight - estimatedMenuHeight / 2 - 20;
  }

  // Check if menu would overflow top
  if (position.y - estimatedMenuHeight / 2 < 20) {
    adjustedY = estimatedMenuHeight / 2 + 20;
  }

  // Calculate bridge height to cover the adjusted distance
  const bridgeHeight = Math.abs(adjustedY - position.y) + 80;

  return (
    <>
      {/* Invisible bridge to prevent hover gaps */}
      <div
        className="fixed bg-transparent pointer-events-auto z-[999999999]"
        style={{
          left: `${position.x - 30}px`,
          top: `${Math.min(position.y, adjustedY) - 40}px`,
          width: '30px',
          height: `${bridgeHeight}px`,
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />

      {/* Popover menu */}
      <div
        className="fixed bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-2xl border border-slate-200/50 min-w-64 max-w-72 max-h-80 overflow-y-auto p-2 animate-slideIn z-[60]"
        style={{
          left: `${position.x + 8}px`,
          top: `${adjustedY}px`,
          transform: 'translateY(-50%)',
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Arrow pointer */}
        <div className="absolute left-0 top-4 transform -translate-x-2 w-4 h-4 bg-white border-l border-t border-slate-200/50 rotate-[-45deg]" />

        {/* Title */}
        {title && (
          <div className="px-3 py-2 mb-1">
            <h3 className="text-sm font-bold text-slate-800">{title}</h3>
          </div>
        )}

        {/* Menu Items */}
        <div className="space-y-1">
          {items.map((item) => {
            const isActive = isSubItemActive(item.href);
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={`group relative flex items-center px-3 py-2.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-[rgb(71,161,62)] to-indigo-500 text-white shadow-md'
                    : 'text-slate-500 hover:bg-gradient-to-r hover:from-slate-100 hover:to-blue-50 hover:text-blue-600'
                }`}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-4 bg-white rounded-r-full" />
                )}

                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-xs block">{item.name}</span>
                  {item.description && (
                    <p
                      className={`text-xs mt-0.5 transition-colors duration-200 ${
                        isActive ? 'text-blue-100' : 'text-slate-400 group-hover:text-blue-500'
                      }`}
                    >
                      {item.description}
                    </p>
                  )}
                </div>
              </NavLink>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-slideIn {
          animation: slideIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default SidebarSubmenu;
